import { useCallback, useMemo } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useMatch, useNavigate, useParams } from 'react-router-dom-v5-compat';

import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { WebhookEvent } from 'dashboard/models/webhook-event';
import {
  clearEvent,
  setCurrentPage,
  setPageSize,
  setWebhookEvents,
  WebhookEventsFilterParams,
} from 'dashboard/redux/actions/event';
import { showMessage } from 'dashboard/redux/actions/messages';
import { RootState } from 'dashboard/redux/store';
import { loadEvent, loadWebhookEventList } from 'dashboard/redux/thunk-actions/event';
import DashboardEventsShowView from 'dashboard/views/DashboardEventsShowView';
import useApiLoader from 'lib/hooks/useApiLoader';
import useOnMountAndUnmount from 'lib/hooks/useOnMountAndUnmount';
import useRedirectOnEnvironmentChange from 'lib/hooks/useRedirectOnEnvironmentChange';
import useTable from 'lib/hooks/useTable';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';

type Params = { eventId: string };

const DashboardEventsShowRoute = (props: PropsFromRedux) => {
  const navigate = useNavigate();
  const { eventId } = useParams<Params>() as Params; // eventId will always be defined

  useOnMountAndUnmount(props.clearEvent);

  useRedirectOnEnvironmentChange('/dashboard/events');

  const { handlePageLinkClick, handlePageSizeClick } = useTable({
    filters: props.filters,
    pageSize: props.pageSize,
    currentPage: props.currentPage,
    setCurrentPage: props.setCurrentPage,
    setPageSize: props.setPageSize,
  });

  const { loading: eventLoading } = useApiLoader({
    loader: () => props.loadEvent(eventId),
    deps: [props.loadEvent, eventId, props.showMessage],
    onNetworkError: () => props.showMessage('Failed to load event.', MessageType.Failure),
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load event.', MessageType.Failure);
      }
    },
  });

  // We force the filter to always only return webhook events for the current event
  const filters: WebhookEventsFilterParams = useMemo(() => ({ eventId }), [eventId]);

  const { loading: webhookEventsLoading } = useApiLoader({
    loader: () =>
      props.loadWebhookEventList({
        currentEnvironment: props.currentEnvironment,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        currentPage: props.currentPage,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        pageSize: props.pageSize,
        filters,
      }),
    deps: [
      props.loadWebhookEventList,
      props.currentEnvironment,
      props.currentPage,
      props.pageSize,
      props.showMessage,
      filters,
    ],
    onNetworkError: () => {
      props.showMessage('Failed to load webhook events.', MessageType.Failure);
    },
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load webhook events.', MessageType.Failure);
      }
    },
  });

  const webhookEventMatch = useMatch('/dashboard/events/:eventId/webhook-events/:webhookEventId');

  const handleOpenWebhookEventModal = useCallback(
    (webhookEvent: WebhookEvent) =>
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      navigate(`/dashboard/events/${props.event.id}/webhook-events/${webhookEvent.id}`),
    [props.event],
  );
  const handleCloseWebhookEventModal = useCallback(
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    () => navigate(`/dashboard/events/${props.event.id}`),
    [props.event],
  );
  // Whenever a WebhookEvent is updated in its modal, make sure to update it in the list too.
  const handleWebhookEventUpdate = useCallback(
    (webhookEvent: WebhookEvent) => {
      const idx = props.webhookEvents.findIndex((we) => we.id === webhookEvent.id);
      props.setWebhookEvents(
        [...props.webhookEvents.slice(0, idx), webhookEvent, ...props.webhookEvents.slice(idx + 1)],
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        props.paginationParams,
      );
    },
    [props.webhookEvents],
  );

  if (eventLoading && props.event == null) {
    return <LoadingIndicator fullscreen={true} />;
  }

  return (
    <DashboardEventsShowView
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      event={props.event}
      activeWebhookEventId={webhookEventMatch?.params.webhookEventId}
      webhookEvents={props.webhookEvents}
      webhookEventsLoading={webhookEventsLoading}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      pageSize={props.pageSize}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      paginationParams={props.paginationParams}
      onPageLinkClick={handlePageLinkClick}
      onPageSizeClick={handlePageSizeClick}
      onWebhookEventUpdate={handleWebhookEventUpdate}
      openModal={handleOpenWebhookEventModal}
      closeModal={handleCloseWebhookEventModal}
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  event: state.event.event,
  webhookEvents: state.event.webhookEvents,
  currentEnvironment: state.currentEnvironment.currentEnvironment,
  pageSize: state.event.pageSize,
  currentPage: state.event.currentPage,
  paginationParams: state.event.paginationParams,
  filters: state.event.filters,
});

const mapDispatchToProps = {
  loadEvent,
  loadWebhookEventList,
  setWebhookEvents,
  setCurrentPage,
  setPageSize,
  clearEvent,
  showMessage,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardEventsShowRoute);
